/**
 * PraticaTimeline — F-005 Phase 5 (Task 5.3).
 *
 * Renders the AuditLog-derived status history for a pratica. Extracted from
 * `arness-sketches/pratica-status/SketchPraticaStatoDetail.tsx` (the sketch's
 * static `PraticaTimeline`) and refined to consume the shared `TimelineEvent[]`
 * shape (`@app/shared`) instead of mock data. Action codes are mapped to Italian
 * labels via `ACTION_LABELS`; the most recent event is marked as current.
 *
 * NOTE: action codes mirror the DB audit `action LIKE 'pratica.stato.%'` events.
 * The DB enum has no `IN_LAVORAZIONE` / `REGISTRATA` / `ARCHIVIATA` values —
 * those keys below are tolerated label fallbacks but the canonical states are
 * `inviata`, `in_carico`, `in_elaborazione`, `bloccata`, `completata`,
 * `annullata` plus the `ri_sottomessa` correction event.
 */
import type { TimelineEvent } from '@app/shared';
import { CheckCircle2, Clock } from 'lucide-react';

interface PraticaTimelineProps {
  events: TimelineEvent[];
}

const ACTION_LABELS: Record<string, string> = {
  'pratica.stato.ri_sottomessa': 'Pratica ri-sottomessa',
  'pratica.stato.inviata': 'Pratica inviata',
  'pratica.stato.in_carico': 'Pratica presa in carico',
  'pratica.stato.in_elaborazione': 'Pratica in lavorazione',
  'pratica.stato.bloccata': 'Pratica bloccata',
  'pratica.stato.completata': 'Pratica registrata',
  'pratica.stato.annullata': 'Pratica annullata',
};

export function PraticaTimeline({ events }: PraticaTimelineProps) {
  if (events.length === 0) {
    return <p className="text-sm text-muted-foreground">Nessun evento registrato.</p>;
  }

  return (
    <ol className="relative space-y-4 border-l border-muted-foreground/20 pl-4">
      {events.map((event, idx) => (
        <li key={event.id} className="relative">
          <span className="absolute -left-2 flex h-4 w-4 items-center justify-center rounded-full bg-background ring-1 ring-muted-foreground/30">
            {idx === events.length - 1 ? (
              <CheckCircle2 className="h-3 w-3 text-primary" aria-hidden="true" />
            ) : (
              <Clock className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
            )}
          </span>
          <div className="ml-2 space-y-0.5">
            <p className="text-sm font-medium">{ACTION_LABELS[event.action] ?? event.action}</p>
            {event.note && <p className="text-xs text-muted-foreground">{event.note}</p>}
            <time className="block text-xs text-muted-foreground" dateTime={event.createdAt}>
              {new Date(event.createdAt).toLocaleDateString('it-IT', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </time>
          </div>
        </li>
      ))}
    </ol>
  );
}
